import { Box, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAllTeamMemberRecordsOfTeam, ITeamFilters, ITeamMembersResponse } from 'wisestep-api';
import { Notification, WsButton, WsPagination, WsTitleBanner } from 'wisestep-base';

import { WsBreadCrumbs } from '../../../components/WsBreadCrumbs';
import { useWorkspaceContext } from '../../../context';
import { teamFilters, teamMembersBreadCrumbsList } from '../helper/defaultValues';

import AddMembersDrawer from './AddMembersDrawer';
import TeamMembersTable from './TeamMembersTable';

const TeamMembersList = () => {
  interface ILocationState {
    teamId: string;
    teamName: string;
    teamOwnerEmail: string;
    isTeamDrawerOpen?: boolean;
  }
  const location = useLocation();
  const isAdmin = useWorkspaceContext().owns;
  const { teamId, teamName, teamOwnerEmail, isTeamDrawerOpen } = location.state as ILocationState;
  const [query, setQuery] = useState<ITeamFilters>(teamFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteMember, setDeleteMember] = useState(false);

  const [teamMembers, setTeamMembers] = useState<ITeamMembersResponse[]>();
  const [totalTeamMembers, setTotalTeamMembers] = useState(0);

  const [openTeamMembersDrawer, setOpenTeamMembersDrawer] = useState<boolean>(
    isTeamDrawerOpen ?? true
  );

  const [addMembersSuccess, setAddMembersSuccess] = useState<boolean>(false);

  const handleOpenTeamMembersDrawer = () => setOpenTeamMembersDrawer(true);

  const handleCloseTeamMembersDrawer = () => setOpenTeamMembersDrawer(false);

  const onSuccessHandler = () => {
    setAddMembersSuccess(true);
  };

  const deleteMemberHandler = () => {
    setDeleteMember(true);
  };

  useEffect(() => {
    const getTeams = async () => {
      const response = await getAllTeamMemberRecordsOfTeam(teamId, query);
      setTeamMembers([
        {
          memberEmail: teamOwnerEmail,
          name: '',
          role: 'owner',
          roles: null,
          teamId: teamId,
        },
        ...response.data.teamMembers,
      ]);
      deleteMember && setDeleteMember(false);
      setTotalTeamMembers(response.data.totalUsersCount);
    };
    getTeams();
  }, [query, teamId, teamOwnerEmail, addMembersSuccess, deleteMember]);

  const updateQuery = (event, value) => {
    const updatedQuery = { ...query };
    updatedQuery.pageNumber = value + 1;
    setQuery(updatedQuery);
    setCurrentPage(value + 1);
  };

  return (
    <>
      <Box>
        <Box py={3}>
          <WsBreadCrumbs breadCrumbsList={teamMembersBreadCrumbsList(teamName)} />
          <WsTitleBanner title={teamName} backButton={false} />
        </Box>
        {isAdmin && (
          <Stack my={3} alignItems="flex-end">
            <WsButton onClick={handleOpenTeamMembersDrawer}>Add Members</WsButton>
          </Stack>
        )}
        <WsPagination
          count={Math.ceil(totalTeamMembers / query?.pageSize)}
          onChange={updateQuery}
          itemsPerPage={query?.pageSize}
          pageNumber={currentPage - 1}
          defaultPage={currentPage}
          isPageCount={true}
          type="TeamMembers"
          totalCount={totalTeamMembers}
        />
        <TeamMembersTable teamMembers={teamMembers || []} onDeleteMember={deleteMemberHandler} />
      </Box>
      <AddMembersDrawer
        teamId={teamId}
        teamName={teamName}
        open={openTeamMembersDrawer}
        onSuccess={onSuccessHandler}
        handleClose={handleCloseTeamMembersDrawer}
      />
      {addMembersSuccess && (
        <Notification
          vertical="top"
          horizontal="center"
          autoHideDuration={3000}
          notificationObject={{
            message: 'Member(s) Added Successfully!',
            type: 'success',
          }}
          notify={true}
        />
      )}
    </>
  );
};

export default TeamMembersList;
